import dayjs from 'dayjs'
import {
  InformalFeedbackReviewDismissReason,
  InformalFeedbackRequest,
  InformalFeedbackRequestCounts,
  InformalFeedbackReview,
  InformalFeedbackReviewState,
  InformalFeedbackViewerState,
  ReviewType,
  SubmittedInformalFeedbackReview,
} from 'api/clients/InformalFeedbackRequestsClient'

/*
 * Format the end date of an informal feedback request:
 *
 * - Ends Today
 * - Ends Tomorrow
 * - Ends in X day(s)
 */
export const displayEndDate = (endDate: string) => {
  const date = dayjs(endDate).endOf('day')
  const today = dayjs()
  const daysToGo = date.diff(today, 'day')

  if (daysToGo === 0) {
    return 'Ends Today'
  }
  if (daysToGo === 1) {
    return 'Ends Tomorrow'
  }
  if (daysToGo === -1) {
    return 'Ended Yesterday'
  }
  if (daysToGo < -1) {
    return `Ended ${Math.abs(daysToGo)} days ago`
  }
  return `Ends in ${daysToGo} ${daysToGo > 1 ? 'days' : 'day'}`
}

export const displayDaysRemaining = (endDate: string) => {
  const date = dayjs(endDate).endOf('day')
  const today = dayjs()
  const daysToGo = date.diff(today, 'day')

  if (daysToGo === 0) {
    return 'Today'
  }
  if (daysToGo === 1) {
    return 'Tomorrow'
  }

  return `in ${daysToGo} ${daysToGo > 1 ? 'days' : 'day'}`
}

/*
 * Format the end date of an informal feedback request:
 *
 * - 1d ago
 * - Today
 * - 2d left
 */
export const displayShortEndDate = (endDate: string) => {
  const date = dayjs(endDate).endOf('day')
  const today = dayjs()
  const daysToGo = date.diff(today, 'day')

  if (daysToGo === 0) {
    return 'Today'
  }
  if (daysToGo < -1) {
    return `${Math.abs(daysToGo)}d ago`
  }
  return `${daysToGo}d left`
}

/*
 * Format the end date value of an informal feedback request to be displayed in an end date picker.
 * Follows the same rules of `displayEndDate`, but handles the case where end date is `null`.
 */
export const displayEndDatePickerValue = (value: string | null) => {
  if (value === null) {
    return 'Select end date'
  }
  return displayEndDate(value)
}

const stateToInteger = {
  [InformalFeedbackReviewState.Pending]: 3,
  [InformalFeedbackReviewState.Submitted]: 2,
  [InformalFeedbackReviewState.Dismissed]: 1,
}

const sortByReviewState = ({ state: stateA }: InformalFeedbackReview, { state: stateB }: InformalFeedbackReview) => {
  const stateAValue = stateToInteger[stateA]
  const stateBValue = stateToInteger[stateB]

  if (stateAValue > stateBValue) {
    return -1
  }

  if (stateAValue < stateBValue) {
    return 1
  }

  return 0
}

export const getReviewAssignedToMe = (request?: InformalFeedbackRequest, userId?: number) => {
  if (request) {
    return [...request.reviews]
      .sort(sortByReviewState)
      .find(
        ({ reviewer, dismissed_reason }) =>
          reviewer.id === userId && dismissed_reason !== InformalFeedbackReviewDismissReason.RemovedFromReview,
      )
  }
}

export const getViewerAssignedToMe = (request?: InformalFeedbackRequest, userId?: number) => {
  if (request) {
    return request.viewers.find(({ viewer }) => viewer.id === userId)
  }
}

const getCount =
  (count: keyof InformalFeedbackRequestCounts) =>
  (request: InformalFeedbackRequest, review: InformalFeedbackReview) => {
    const { counts } = request
    const {
      groups: { users },
    } = counts
    const userCounts = users[review.reviewer.id || -1] || {}
    return userCounts[count] || 0
  }

export const getReviewCommentsCount = getCount('comments_count')

export const getReviewTodosCount = getCount('todos_count')

export const getActiveReviews = ({ reviews }: InformalFeedbackRequest) =>
  reviews.filter(({ state }) => state !== InformalFeedbackReviewState.Dismissed)

export const getRequestedReviews = ({ reviews }: InformalFeedbackRequest) =>
  reviews.filter(
    ({ state, dismissed_reason }) =>
      state !== InformalFeedbackReviewState.Dismissed ||
      (state === InformalFeedbackReviewState.Dismissed &&
        dismissed_reason === InformalFeedbackReviewDismissReason.Declined),
  )

export const getActiveRequiredReviews = (reviews: InformalFeedbackRequest) => {
  const activeReviews = getActiveReviews(reviews)
  return activeReviews.filter((review) => review.required === true)
}

const sortBySubmittedAt = (
  { submitted_at: submittedAtA }: SubmittedInformalFeedbackReview,
  { submitted_at: submittedAtB }: SubmittedInformalFeedbackReview,
) => {
  if (dayjs(submittedAtA).isBefore(submittedAtB)) {
    return 1
  }

  if (dayjs(submittedAtA).isAfter(submittedAtB)) {
    return -1
  }

  return 0
}

const sortByReverseScore = (reviewA: SubmittedInformalFeedbackReview, reviewB: SubmittedInformalFeedbackReview) => {
  const { score: scoreA } = reviewA
  const { score: scoreB } = reviewB

  if (scoreA === scoreB) {
    return sortBySubmittedAt(reviewA, reviewB)
  }

  if (scoreA > scoreB) {
    return 1
  }

  if (scoreA < scoreB) {
    return -1
  }

  return 0
}

export const getSubmittedReviews = (request: InformalFeedbackRequest) => {
  const submittedReviews = getActiveReviews(request).filter(
    ({ state }) => state === InformalFeedbackReviewState.Submitted,
  ) as SubmittedInformalFeedbackReview[]
  return [...submittedReviews].sort(sortByReverseScore)
}

export const getCompletedViewers = (request: InformalFeedbackRequest) => {
  return request.viewers.filter(({ state }) => state === InformalFeedbackViewerState.Completed)
}

export const getCompletedViewersCount = (request: InformalFeedbackRequest) => {
  return getCompletedViewers(request).length
}

export const getPendingReReviews = (request: InformalFeedbackRequest) => {
  const pendingReviews = getActiveReviews(request).filter(
    ({ state, iteration }) => state === InformalFeedbackReviewState.Pending && iteration > 0,
  ) as SubmittedInformalFeedbackReview[]
  return [...pendingReviews]
}

export const isReadReceiptRequest = (request: InformalFeedbackRequest) => request.type === ReviewType.ReadReceipt
